/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable max-len */
import React, { useState, useEffect, useRef } from 'react';
import cc from 'classnames';
import { useTheme } from '@material-ui/core/styles';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useHistory } from 'react-router-dom';
import Loader from 'ui/Loader/Loader';
import api from 'api/api';
import Button, { Themes as ButtonThemes, Sizes as ButtonSizes } from 'ui/Button/Button';
import auth from 'utils/auth';
import noop from 'utils/noop';
import routePaths from 'routes/routePaths';
import { IApiResponse } from 'utils/models';
import { AxiosStatus } from 'api/axiosConfig';
import TransactionDetails from 'components/modals/TransactionDetails/TransactionDetails';
import { ITransactionField } from 'ui/structure/TransactionCard';
import DateEvent from 'ui/TableInputs/DateEvent/DateEvent';
import { formatTwoDigitDate, amountFormatter } from 'utils/formatters';
import { getTransactionsArray } from './pendingDataMapper';
import { IApiPendingTransaction, IPendingTransactions } from './Beneficiary.model';
import { transferConfirmationValues, mobileMoneyConfirmationValues, bulkTransferConfirmationValues,
  prepaidCardConfirmationValues, FXTransferConfirmationValues,
  defaultConfirmationValues } from './confirmationValues';

import styles from './Beneficiaries.module.css';
import arrow from 'assets/icons/arrow-right.png';

type pageDetails = {
  pageNumber: number;
  pageSize: number;
};

const Beneficiaries = () => {
  const [data, setData] = useState<IPendingTransactions[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const gridRef = useRef<AgGridReact<IPendingTransactions>>(null);
  const [showAllData, setShowAllData] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [confirmationData, setConfirmationData] = useState<ITransactionField[]>([]);
  const [isOpenTrxnDetailModal, setOpenTrxnDetailModal] = useState(false);
  const [transactionDetailModal, setTransactionDetailModal] = useState<IPendingTransactions>();
  const [loadingStatus, setLoadingStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);

  const emptyMessage = "There're no Pending Transactions.";
  const pendingLoading = loadingStatus === AxiosStatus.PENDING;
  const successLoaded = loadingStatus === AxiosStatus.SUCCESS;

  const closeTrxnDetailModal = () => {
    setOpenTrxnDetailModal(false);
    setTransactionDetailModal(undefined);
    setLoadingData(false);
  };

  // Requesting Data for Table
  function requestDataForTable(): void {
    gridRef.current?.api?.showLoadingOverlay();
    function successHandler(response: IApiPendingTransaction): void {
      setLoadingStatus(AxiosStatus.SUCCESS);
      setData(response.transactions.slice(0, 10));
      setSubmitting(false);
    }

    function errorHander(error: IApiResponse): void {
      console.error(error);
      setLoadingStatus(AxiosStatus.FAILURE);
      gridRef.current?.api.hideOverlay();
    }
    const requestBody: pageDetails = {
      pageNumber: 1,
      pageSize: 10,
    };
    api.post('transfer/allPendingTransactions', requestBody).then(successHandler).catch(errorHander);
  }

  useEffect(requestDataForTable, []);

  const history = useHistory();
  const roles = auth.data?.roles;
  const roleNames = roles ? roles.map((item) => item.name) : [];
  const toPayment = () => {
    const toPending = {
      subTab: 0,
      innerTab: true,
    };
    history.push({ pathname: routePaths.pendingRequests, state: toPending });
  };

  const theme = useTheme();
  const showButton = useMediaQuery(theme.breakpoints.down('lg'));

  const showData = () => setShowAllData(true);
  const getHeight = () => {
    if (showButton && !showAllData) {
      return 224;
    }
    if (showButton && showAllData) {
      return 510;
    }
    return 274;
  };

  const handleShowTrxModal = (transaction: IPendingTransactions) => () => {
    setOpenTrxnDetailModal(true);
    setTransactionDetailModal(transaction);
    const cardValues = {
      ...transaction,
    };
    let confirmationValues: string[];

    switch (transaction.transactionType) {
      case 'INSTANT-TRANSFER-TO-OTHER-BANK':
      case 'OTHERBANK-TRANSFER':
      case 'LOCAL-TRANSFER':
      case 'OWN-TRANSFER':
        confirmationValues = transferConfirmationValues;
        break;
      case 'MOBILEMONEY-TRANSFER':
        confirmationValues = mobileMoneyConfirmationValues;
        break;
      case 'TRANSFER':
        confirmationValues = bulkTransferConfirmationValues;
        break;
      case 'PREPAIDCARD-TRANSFER':
        confirmationValues = prepaidCardConfirmationValues;
        break;
      case 'SERVICE-CHARGE':
        confirmationValues = FXTransferConfirmationValues;
        break;
      default:
        confirmationValues = defaultConfirmationValues;
    }
    setConfirmationData(getTransactionsArray(cardValues, undefined, undefined, confirmationValues));
  };

  const renderActions = (prop: ICellRendererParams<IPendingTransactions, number | string>) => {
    if (prop.data) {
      return (
        <div
          className={styles.toDetailsButton}
          role="button"
          tabIndex={0}
          onKeyPress={noop}
          onClick={handleShowTrxModal(prop.data)}
        >
          <span className={styles.btnText}>To Details</span>
          <img src={arrow} alt="arrow" />
        </div>
      );
    }
    return false;
  };

  const renderScheduleLink = (prop: ICellRendererParams<IPendingTransactions, number | string>) => {
    if (prop.data) {
      return <div>{String(prop.data?.scheduleId)}</div>;
    }
    return false;
  };
  const renderAmount = (prop: ICellRendererParams<IPendingTransactions>) => {
    if (prop.data) {
      const currency = prop.data?.currencyCode;
      const amount = prop.data?.transactionAmount || prop.data?.totalAmount;
      if (currency) {
        return <div>{amountFormatter(currency, false)(String(amount !== null ? amount : 0))}</div>;
      }
      return <div>{amountFormatter('ghs', false)(String(amount !== null ? amount : 0))}</div>;
    }
    return null;
  };
  const renderCreated = (prop: ICellRendererParams<IPendingTransactions>) => {
    if (prop.data) {
      const formattedDate = formatTwoDigitDate(prop.data?.createDate);
      return <DateEvent name={prop.data?.createdBy} date={formattedDate} />;
    }
    return null;
  };

  const columnDefs: ColDef[] = [
    { field: 'scheduleID', headerName: 'Schedule ID', flex: 1, minWidth: 200, cellRenderer: renderScheduleLink },
    { field: 'totalAmount', headerName: 'Amount', flex: 1, minWidth: 120, cellRenderer: renderAmount },
    { field: 'created', headerName: 'Created', flex: 1, minWidth: 200, cellRenderer: renderCreated },
    { field: 'action', headerName: '', maxWidth: 140, cellRenderer: renderActions },
  ];

  return (
    <>
      {pendingLoading && submitting && <Loader />}
      {/* <pre>{JSON.stringify(data, null, 2)}</pre> */}
      <div className={styles.content}>
        <div className={styles.headerContainer}>
          <div className={styles.title}>Pending Payments</div>
          {roleNames.includes('Authorizer') && !loadingData && (
            <Button
              type="button"
              className={styles.btnSubmit}
              size={ButtonSizes.small}
              theme={ButtonThemes.secondary}
              onClick={toPayment}
            >
              View All Requests
            </Button>
          )}
        </div>
        <div className={styles.itemsWrapper}>
          <div style={{ height: getHeight() }}>
            {data.length && successLoaded ? (
              <div className={cc('tableWrapper', 'ag-theme-material')}>
                <AgGridReact
                  ref={gridRef}
                  rowData={data}
                  loadingOverlayComponent={Loader}
                  columnDefs={columnDefs}
                  defaultColDef={{ resizable: true }}
                  suppressCellFocus
                  enableCellTextSelection
                  headerHeight={40}
                  rowHeight={50}
                  undoRedoCellEditing
                  suppressRowHoverHighlight
                  suppressPaginationPanel
                  suppressAutoSize
                  suppressScrollOnNewData
                  domLayout="autoHeight"
                />
              </div>
            ) : (
              <div className={styles.emptyMessage}>{emptyMessage}</div>
            )}
          </div>
          {transactionDetailModal && (
            <TransactionDetails data={confirmationData} open={isOpenTrxnDetailModal} onClose={closeTrxnDetailModal} />
          )}
        </div>
        {showButton && !showAllData && data.length > 3 && !submitting && (
          <Button onClick={showData} size={ButtonSizes.small} className={styles.showButton} fullWidth>
            Show All
          </Button>
        )}
      </div>
    </>
  );
};

export default Beneficiaries;
